<template>
  <div class="fu-drform" data-test="fu-drawer-text-form">
    <div class="fu-drform__from" data-test="fu-drawer-text-from">
      <fu-icon name="text" :size="14" />
      <span v-if="fromNumber">Sends from <strong>{{ fromNumber }}</strong>, your practice’s texting number</span>
      <span v-else>Sends from your practice’s texting number</span>
    </div>

    <div>
      <label class="fu-drform__label" :for="`${uid}-tpl`">Template</label>
      <select
        v-if="templates.length"
        :id="`${uid}-tpl`"
        class="fu-drform__select"
        :value="templateId === null || templateId === undefined ? '' : String(templateId)"
        data-test="fu-drawer-template"
        @change="onTemplate($event.target.value)"
      >
        <option value="" disabled>Pick a template</option>
        <option v-for="t in templates" :key="t.value" :value="String(t.value)">{{ t.label }}</option>
      </select>
      <div v-else class="fu-drform__warn" data-test="fu-drawer-template-empty">
        <fu-icon name="alert" :size="15" />
        <span>No text templates yet — create one in the Wiki, tag it, then come back. <a href="/app/wiki">Open the Wiki</a></span>
      </div>
    </div>

    <div>
      <label class="fu-drform__label" :for="`${uid}-msg`">Message</label>
      <textarea
        :id="`${uid}-msg`"
        class="fu-drform__textarea"
        rows="4"
        :value="message"
        placeholder="Pick a template, then edit the text if you need to"
        data-test="fu-drawer-message"
        @input="$emit('message-input', $event.target.value)"
      ></textarea>
      <p class="fu-drform__hint">Saved to the commlog as this follow-up’s note.</p>
      <p v-if="previewNote" class="fu-drform__preview" data-test="fu-drawer-link-preview">
        The text the patient receives will carry a shortened link: <span>“{{ previewNote }}”</span>
      </p>
      <div class="fu-drform__guidance"><slot name="guidance" /></div>
    </div>

    <slot />
  </div>
</template>

<script>
/**
 * DrawerTextForm — the Text method (today's SMS form, CreateFollowup.vue:499–579, in the design's
 * look): the practice's sending number, the template picker, the message, the short-link preview
 * (renderSmsTemplate's `preview`), and two slots:
 *   guidance  ⛔ the container mounts the EXISTING <SmsGuidance :text="message" substitute-tags> here —
 *             the credit counter and the over-the-carrier-limit block. Never re-implemented in this
 *             file (sms-cost-no-prompt: one counter, no cost prompt).
 *   default   the next-date control.
 *
 *   fromNumber  the practice's texting number, already formatted
 *   templates   [{ value: WikiPageID, label: Name }] — drawerMethods.templatesFor('sms', …)
 *   templateId, message, previewNote
 * Events: template-change(id), message-input(text). Presentational.
 */
import FuIcon from '../FuIcon.vue';

let seq = 0;

export default {
  name: 'DrawerTextForm',
  components: { FuIcon },
  props: {
    fromNumber: { type: String, default: '' },
    templates: { type: Array, default: () => [] },
    templateId: { type: [Number, String], default: null },
    message: { type: String, default: '' },
    previewNote: { type: String, default: '' },
  },
  data() {
    seq += 1;
    return { uid: `fu-drtext-${seq}` };
  },
  methods: {
    onTemplate(v) {
      const t = this.templates.find(x => String(x.value) === v);
      this.$emit('template-change', t ? t.value : null);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './drawer';

.fu-drform { display: flex; flex-direction: column; gap: 16px; }
.fu-drform__label { @include fu-dr-field-label; }
.fu-drform__select { @include fu-input; }
.fu-drform__textarea { @include fu-dr-textarea; }
.fu-drform__hint { @include fu-dr-hint; }

.fu-drform__from {
  @include fu-dr-soft-note;
  align-items: center;
  strong { font-weight: $fu-fw-bold; color: $fu-text; }
  .fu-icon { flex: none; color: $fu-text-subtle; }
}

.fu-drform__warn {
  @include fu-dr-warn-strip;
  .fu-icon { flex: none; margin-top: 1px; color: $fu-warning; }
  a { color: $fu-link; font-weight: $fu-fw-semibold; }
}

.fu-drform__preview {
  margin: 8px 0 0;
  padding: 8px 10px;
  border-radius: $fu-radius-sm;
  background: $fu-info-subtle;
  font-size: 12px;
  line-height: 1.45;
  color: $fu-neutral-700;
  span { overflow-wrap: anywhere; }
}

.fu-drform__guidance { margin-top: 8px; }

.fu-dr--phone .fu-drform__select,
.fu-dr--phone .fu-drform__textarea { @include fu-dr-phone-input; }
</style>
